import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import dayjs from 'dayjs';
import { DashboardSisbenService } from '../../../services/database/sisben/dashboard-sisben.service';
import {
  IGetDashboardLineaTiempoSisbenByRango,
  IGetDashboardTramitesSisbenByRangoFecha
} from '../../../shared/interfaces/database/sisben';
import { IGetDashboardGlobalTramites } from '../../../shared/interfaces/database/tramite';

import { ChartBarComponent, ChartBarConfig } from '../components/chart-bar/chart-bar.component';
import {
  ChartDonutComponent,
  ChartDonutConfig,
  ChartDonutSlice
} from '../components/chart-donut/chart-donut.component';
import { ChartLineComponent, ChartLineConfig } from '../components/chart-line/chart-line.component';
import {
  ChartRadialGaugeComponent,
  ChartRadialGaugeConfig
} from '../components/chart-radial-gauge/chart-radial-gauge.component';
import {HEX_COLOR_PALETTE} from "../components/utils/color-palette";

@Component({
  selector: 'app-dashboard-general-sisben',
  standalone: true,
  imports: [
    TranslatePipe,
    ChartBarComponent,
    ChartDonutComponent,
    ChartLineComponent,
    ChartRadialGaugeComponent
  ],
  templateUrl: './dashboard-general-sisben.component.html',
  styleUrl: './dashboard-general-sisben.component.scss'
})
export class DashboardGeneralSisbenComponent implements OnInit, AfterViewInit {

  @ViewChild('chartBarTramites') chartBarTramites!: ChartBarComponent;
  @ViewChild('chartDonutGlobal') chartDonutGlobal!: ChartDonutComponent;
  @ViewChild('chartRadialGaugeEfectividad') chartRadialGaugeEfectividad!: ChartRadialGaugeComponent;
  @ViewChild('chartLineHistorico') chartLineHistorico!: ChartLineComponent;

  barTramitesConfig!: ChartBarConfig;
  chartDonutConfig!: ChartDonutConfig;
  radialGaugeEfectividadConfig!: ChartRadialGaugeConfig;
  lineHistoricoConfig!: ChartLineConfig;

  fechaInicial: Date = dayjs().startOf('year').toDate();
  fechaFinal: Date = new Date();

  private tramitesData: IGetDashboardTramitesSisbenByRangoFecha[] = [];
  private globalTramitesData: IGetDashboardGlobalTramites[] = [];
  private rawGlobalTramitesData: IGetDashboardGlobalTramites[] = [];
  private historicoMesData?: { datasets: any[][]; allMonths?: any[] };

  private readonly colors: string[] = HEX_COLOR_PALETTE;

  constructor(private dashboardService: DashboardSisbenService) {
    this.initBarTramitesConfig();
    this.initChartDonutConfig();
    this.initRadialGaugeEfectividadConfig();
    this.initLineHistoricoConfig();
  }

  //#region Lifecycle
  ngOnInit(): void {
    this.loadDataSets();
  }

  ngAfterViewInit(): void {
    if (this.tramitesData?.length && this.chartBarTramites) {
      this.chartBarTramites.load(this.tramitesData);
    }
    if (this.globalTramitesData?.length && this.chartDonutGlobal) {
      this.loadChartDonut(this.globalTramitesData);
    }
    if (this.rawGlobalTramitesData?.length && this.chartRadialGaugeEfectividad) {
      this.loadEfectividadGlobal(this.rawGlobalTramitesData);
    }
    if (this.historicoMesData && this.chartLineHistorico) {
      this.chartLineHistorico.load(
        this.historicoMesData.datasets,
        this.historicoMesData.allMonths?.length ? this.historicoMesData.allMonths : undefined
      );
    }
  }
  //#endregion Lifecycle

  //#region Configurations
  private initBarTramitesConfig(): void {
    this.barTramitesConfig = {
      chartId: 'chart-bar-tramites-sisben',
      categoryField: 'tramite',
      showLegend: true,
      barHeight: 55,
      xLabelFormatter: (v: number) => `${v}`,
      tooltipValueFormatter: (v: number) => `${v}`,
      bars: [
        { field: 'recibidos', name: 'Recibidos', color: '#0991EC', showValueLabel: true },
        { field: 'pendientes', name: 'Pendientes', color: '#00e5ff', showValueLabel: true },
        { field: 'finalizados', name: 'Finalizados', color: '#1655B6', showValueLabel: true },
      ],
      data: [],
    };
  }

  private initChartDonutConfig(): void {
    this.chartDonutConfig = {
      chartId: 'donut-global-tramites-sisben',
      slices: [],
      centerLabel: '',
    };
  }

  private initRadialGaugeEfectividadConfig(): void {
    this.radialGaugeEfectividadConfig = {
      chartId: 'radial-gauge-efectividad-sisben',
      percentage: 0,
      centerSub: '0 / 0',
      height: 240,
    };
  }

  private initLineHistoricoConfig(): void {
    this.lineHistoricoConfig = {
      chartId: 'chart-historico-tramite-sisben',
      categoryField: 'mes',
      xLabelRotation: 45,
      series: [
        { field: 'valor', name: 'Tr\u00e1mites', color: this.colors[0] },
        { field: 'valor', name: 'Inclusi\u00f3n', color: this.colors[1] },
        { field: 'valor', name: 'Modificaci\u00f3n', color: this.colors[2] },
        { field: 'valor', name: 'Retiro', color: this.colors[3] },
      ],
      data: [],
    };
  }
  //#endregion Configurations

  //#region Load Data Handlers
  private loadChartDonut(data: IGetDashboardGlobalTramites[]): void {
    if (!this.chartDonutGlobal) return;
    const value: ChartDonutSlice[] = data.map((item, idx) => ({
      label: item.tipo,
      value: item.total,
      color: this.colors[idx % this.colors.length],
    }));
    const finalizadosItem = data?.find(it => it.tipo.toLowerCase().includes('finalizados'));

    this.chartDonutConfig.centerLabel = finalizadosItem ? `${finalizadosItem.total}` : '';
    this.chartDonutConfig.centerSub = 'Finalizados';
    this.chartDonutGlobal.load(value);
  }

  private loadEfectividadGlobal(values: IGetDashboardGlobalTramites[]): void {
    if (!this.chartRadialGaugeEfectividad) return;
    const recibidos = values.find(it => it.tipo.toLowerCase() === 'recibidos');
    const finalizados = values.find(it => it.tipo.toLowerCase() === 'finalizados');
    const recibidosTotal = recibidos?.total ?? 0;
    const finalizadosTotal = finalizados?.total ?? 0;
    const pct = recibidosTotal > 0 ? (finalizadosTotal / recibidosTotal) * 100 : 0;
    const pctRounded = +(pct.toFixed(1));

    this.chartRadialGaugeEfectividad.load({
      percentage: pctRounded,
      centerLabel: `${pctRounded}%`,
      centerSub: `${recibidosTotal} \n Solicitudes recibidas`,
    });
  }
  //#endregion Load Data Handlers

  //#region API Calls
  getDashboardNumeroTramitesSisben(): void {
    const [startDate, endDate] = this.getParamsDates();
    firstValueFrom(
      this.dashboardService.getDashboardTramitesSisbenByRangoFecha(startDate, endDate)
    ).then(response => {
      if (!response) return;
      this.tramitesData = response;
      this.chartBarTramites?.load(response);
    }).catch(error => {
      console.error('Error cargando tr\u00e1mites sisben:', error);
    });
  }

  getDashboardGlobalTramites(): void {
    const [startDate, endDate] = this.getParamsDates();
    firstValueFrom(
      this.dashboardService.getDashboardGlobalTramitesByRangoFecha(startDate, endDate)
    ).then(value => {
      if (!value) return;
      this.rawGlobalTramitesData = value;
      this.globalTramitesData = value;
      this.loadChartDonut(value);
      this.loadEfectividadGlobal(value);
    }).catch(error => {
      console.error('Error cargando global de tr\u00e1mites sisben:', error);
    });
  }

  getDashboardTramitesPorMesByRangoFecha(): void {
    const [startDate, endDate] = this.getParamsDates();
    firstValueFrom(
      this.dashboardService.getDashboardNumeroTramitesSisbenPorMesByRangoFecha(startDate, endDate)
    ).then((response: IGetDashboardLineaTiempoSisbenByRango[]) => {
      if (!response || !response.length) return;

      // Configurar las series con los nombres devueltos por el backend
      this.lineHistoricoConfig.series = response.map((serie, idx) => ({
        field: 'valor',
        name: serie.descripcion || `Serie ${idx + 1}`,
        color: this.colors[idx % this.colors.length],
        dashed: false,
      }));

      // Extraer los datasets per-series
      const datasets = response.map(serie =>
        (serie.values ?? []).sort((a,b )=> a>b ? 1 : -1).map(v => ({
          mes: v.mes,
          valor: (v as any).valor ?? (v as any).value ?? 0,
        }))
      );

      // Unificar meses en el eje X
      const allMonths = Array.from(
        new Set(response.flatMap(s => (s.values ?? []).map(v => v.mes)))
      ).map(mes => ({ mes }));

      this.historicoMesData = { datasets, allMonths };

      if (this.chartLineHistorico) {
        this.chartLineHistorico.load(datasets, allMonths.length ? allMonths : undefined);
      }
    }).catch(error => {
      console.error('Error cargando hist\u00f3rico de tr\u00e1mites por mes sisben:', error);
    });
  }
  //#endregion API Calls

  //#region Filter Events & Helpers


  private loadDataSets(): void {
    this.getDashboardNumeroTramitesSisben();
    this.getDashboardGlobalTramites();
    this.getDashboardTramitesPorMesByRangoFecha();
  }

  private getParamsDates(): [string, string] {
    return [this.castToString(this.fechaInicial), this.castToString(this.fechaFinal)];
  }

  private castToString(date: Date): string {
    return date.toISOString().substring(0, date.toISOString().indexOf('T'));
  }
  //#endregion Filter Events & Helpers
}
